// ---------------------------------------------------------------------------
// F3b C2: carga da série nova EM VOLTA de um horário T (troca de timeframe ou de ativo no mesmo momento).
//
// A fonte só sabe pedir "as `limit` velas com abertura < before" (CandleSource). Em volta de T:
//   before = T + ceil(limit/2)·iv  → metade do bloco depois de T (a vela que contém T inclusive), metade antes.
// A janela pode não ser contígua com o presente (as velas ao vivo vão para a cauda; F3a C3). Se o pedido passa do
// agora, é a carga do presente de sempre.
//
// T antes do início do ativo (a fonte não tem nada antes de `before`): procura o bloco do INÍCIO por busca binária no
// tempo entre esse `before` (vazio) e o bloco do presente. Um bloco com menos de `limit` velas é candidato; o início
// só vale quando a fonte confirma (pedido antes da vela mais antiga dele volta vazio, como o fim real do F3a). Falha
// devolve `erro`, nunca início. Achado o início, um bloco cheio a partir dele.
//
// Puro: a fonte é injetada (testável em Node com uma fonte sintética).
// ---------------------------------------------------------------------------

import { MAX_CANDLES_PER_REQUEST, type CandleSource, type CandleErrorKind } from './candleSource';
import { NAV_VISIBLE_CANDLES } from './historyNav';
import { AHEAD_IDLE } from './windowPolicy';
import type { RawCandle } from './seriesWindow';

export type SerieEmVolta =
  | {
      kind: 'ok';
      candles: RawCandle[];
      /** a vela mais nova do bloco é a de agora (a janela contém o presente; as velas ao vivo entram nela) */
      atPresent: boolean;
      /** horário da primeira vela da FONTE, quando o bloco a contém e a fonte confirmou nada antes (null = não se sabe) */
      startKnown: number | null;
      /** quantos pedidos foram feitos */
      requests: number;
    }
  | { kind: 'erro'; tipo: CandleErrorKind; status: number | null; message: string; requests: number };

export interface SerieEmVoltaReq {
  symbol: string;
  interval: string;
  intervalMs: number;
  /** horário do centro; null = presente */
  T: number | null;
  /** agora (relógio do app) */
  now: number;
  limit: number;
}

/** Limite de pedidos da busca do início (≈ log2 de 2^20 blocos: sobra para qualquer ativo real). */
export const MAX_START_SEARCH = 24;

/** `before` do bloco em volta de T. */
export function beforeAround(T: number, intervalMs: number, limit: number): number {
  return T + Math.ceil(limit / 2) * intervalMs;
}

/** O bloco chega ao presente? (mesma regra da costura do F3a: a mais nova é a de agora ou a anterior) */
export function blockAtPresent(newestTime: number, intervalMs: number, now: number): boolean {
  return newestTime >= now - 2 * intervalMs;
}

export async function carregarEmVolta(src: CandleSource, req: SerieEmVoltaReq): Promise<SerieEmVolta> {
  const { symbol, interval, intervalMs: iv, T, now, limit } = req;
  let requests = 0;
  type Fail = Extract<SerieEmVolta, { kind: 'erro' }>;
  const fetch = (before?: number) => { requests++; return src.fetchBlock({ symbol, interval, before, limit }); };
  const fail = (r: { tipo: CandleErrorKind; status: number | null; message: string }): Fail => ({ kind: 'erro', tipo: r.tipo, status: r.status, message: r.message, requests });
  const ok = (candles: RawCandle[], startKnown: number | null, present: boolean): SerieEmVolta => ({ kind: 'ok', candles, atPresent: present, startKnown, requests });
  const noCandles = (): Fail => ({ kind: 'erro', tipo: 'outro', status: null, message: 'fonte sem velas', requests });

  // presente: as mais novas da fonte (sem T, ou o bloco em volta de T passaria do agora)
  const pr0 = T === null || beforeAround(T, iv, limit) > now ? await fetch(undefined) : null;
  let presentNewest = pr0 && pr0.kind === 'ok' && pr0.candles.length ? pr0.candles[pr0.candles.length - 1][0] : Infinity;
  const isPresent = (c: RawCandle[]) => c[c.length - 1][0] >= presentNewest || blockAtPresent(c[c.length - 1][0], iv, now);

  /**
   * Bloco com menos de `limit` velas e T antes da primeira: candidato a conter o início da fonte. Confirma (pedido
   * antes da mais antiga volta vazio, como o fim real do F3a) e traz um bloco cheio a partir dele. 'nao' = há mais
   * antigas (o filtro "< before" do cliente pode tirar uma vela de um bloco cheio); null = não é candidato.
   */
  const startFrom = async (candles: RawCandle[]): Promise<SerieEmVolta | 'nao' | null> => {
    if (candles.length >= limit || T === null || T >= candles[0][0]) return null;
    const c = await fetch(candles[0][0]);
    if (c.kind === 'erro') return fail(c);
    if (c.kind === 'ok' && c.candles.length > 0) return 'nao';
    const S = candles[0][0];
    if (isPresent(candles)) return ok(candles, S, true);       // a série inteira já veio (série curta)
    const full = await fetch(S + (limit - 1) * iv + 1);         // aberturas S .. S + (limit-1)·iv
    if (full.kind === 'erro') return fail(full);
    const got = full.kind === 'ok' && full.candles.length > 0 ? full.candles : candles;
    return ok(got, S, isPresent(got));
  };

  if (pr0) {
    if (pr0.kind === 'erro') return fail(pr0);
    if (pr0.kind !== 'ok' || pr0.candles.length === 0) return noCandles();
    const st = await startFrom(pr0.candles);
    if (st && st !== 'nao') return st;
    return ok(pr0.candles, null, true);
  }

  const before = beforeAround(T!, iv, limit);
  const r = await fetch(before);
  if (r.kind === 'erro') return fail(r);
  if (r.kind === 'ok' && r.candles.length > 0) {
    const st = await startFrom(r.candles);
    if (st && st !== 'nao') return st;
    return ok(r.candles, null, isPresent(r.candles));
  }

  // nada antes de `before`: T é anterior ao início do ativo. Busca o bloco do início entre `lo` (vazio) e o presente.
  let lo = before;
  const pr = await fetch(undefined);
  if (pr.kind === 'erro') return fail(pr);
  if (pr.kind !== 'ok' || pr.candles.length === 0) return noCandles();
  presentNewest = pr.candles[pr.candles.length - 1][0];
  const p0 = await startFrom(pr.candles);
  if (p0 && p0 !== 'nao') return p0;
  let hi = pr.candles[0][0];                                 // há velas com abertura >= hi; o início está em [lo, hi]
  for (let k = 0; k < MAX_START_SEARCH; k++) {
    // intervalo pequeno: o bloco que termina em hi tem tudo de [lo, hi)
    const tight = hi - lo <= limit * iv;
    const mid = tight ? hi : lo + Math.floor((hi - lo) / 2 / iv) * iv;
    const m = await fetch(mid);
    if (m.kind === 'erro') return fail(m);
    if (m.kind !== 'ok' || m.candles.length === 0) { lo = mid; if (tight) break; continue; }
    const st = await startFrom(m.candles);
    if (st && st !== 'nao') return st;
    hi = m.candles[0][0];
  }
  // não achou dentro do limite de pedidos (só com uma fonte incoerente): fica no presente, início desconhecido
  return ok(pr.candles, null, true);
}

// ---------------------------------------------------------------------------
// F3d: troca completa em paralelo e abertura única. Decisão do Mateus (31/10): esperar tudo e mostrar de uma vez,
// sem a força mudar de forma depois. O F3c abria com 2 blocos e a força repintava até 38% quando o resto chegava
// (o cálculo depende da janela inteira: escala sobre todas as velas, Kalman desde a 1ª, Hilbert com borda). O C1 do
// F3d (tests/force-margin.mts) mediu: só a janela FINAL (~10.000 velas, para onde a pré-carga a leva) fica <= 0,7%.
//
// Janela final [ini, fim): W velas (o limite da janela, 10.000), em volta da vela de T ou terminando no presente.
// Assimétrica para a pré-carga do F3a já estar satisfeita na abertura (sem carga + despejo logo depois): do lado da
// direção do movimento AHEAD_IDLE velas além da vista (+ o aquecimento, se for o passado); do outro, o resto
// (3.950 >= KEEP). Sem direção, o passado (a prioridade de antes). Perto do presente, a janela encosta nele.
//
// Pedidos: os blocos da GRADE do timeframe que cobrem a janela (bloco b = aberturas em [b·G, (b+1)·G), G = o tamanho
// máximo do pedido da fonte × intervalo, contado da época; os mesmos para qualquer usuário e troca repetida: base do
// cache do F4); o bloco que contém o agora vai como o pedido do presente (mudaria a cada vela). Até MAX_BLOCKS (11)
// por troca, no máximo MAX_IN_FLIGHT (6) em voo, o resto na fila. Juntados e cortados em [ini, fim).
//
// "before" = fim do bloco − 1 ms (a fonte devolve as `limit` velas com abertura <= endTime; o cliente fica com as
// < before): o bloco inteiro, sem buraco entre blocos.
//
// Falha de um pedido: só ele de novo, depois de 2 s e de 4 s. Com 3 falhas na troca, ou 12 s sem terminar, a carga
// devolve `erro` (o app mantém o gráfico anterior e mostra "tentar de novo"; nunca marca fim); o que já chegou fica
// guardado e o "tentar de novo" pede só o que falta (o do presente sempre de novo: o ao vivo começa na vela de agora).
//
// Começo do ativo nos blocos (bloco com menos velas que o pedido, ou vazio): abre com o que chegou quando basta (cobre
// as velas da vista, ou vai até o presente, ou tem as 1.051 velas a partir do começo; o início NÃO é marcado: a
// pré-carga confirma com a fonte, como no F3a). Tudo vazio (T antes do começo): um pedido do presente; curto = a
// série inteira. Ativo longo que começou depois de T: a busca do início com confirmação (`carregarEmVolta`).
// ---------------------------------------------------------------------------

/** Pedido da grade = o tamanho máximo de um pedido da fonte (não um 1.000 fixo: outra fonte terá outro limite). */
export const GRID_CANDLES = MAX_CANDLES_PER_REQUEST;
/** velas mais antigas que a do centro para a vista: 499 visíveis + 50 de aquecimento + 1 */
export const NEED_OLDER = 550;
/** a vela do centro e as 500 mais novas visíveis */
export const NEED_NEWER = 501;
/** velas de aquecimento que o cálculo descarta no começo da série */
export const WARMUP = 50;
/** pedidos por troca: a janela de 10.000 em blocos + o do presente */
export const MAX_BLOCKS = 11;
/** pedidos em voo ao mesmo tempo numa troca (o resto espera na fila) */
export const MAX_IN_FLIGHT = 6;
/** espera antes de repetir um pedido que falhou (1ª e 2ª vez) */
export const RETRY_DELAYS = [2000, 4000];
/** falhas numa troca até desistir ("tentar de novo") */
export const MAX_FAILS = 3;
/** tempo máximo de uma tentativa de troca até desistir ("tentar de novo") */
export const GIVE_UP_MS = 12000;

export interface PedidoGrade {
  /** chave estável do pedido na troca (o "tentar de novo" pede só as que faltam) */
  key: string;
  kind: 'bloco' | 'presente' | 'complemento';
  /** ausente = as mais novas (presente) */
  before?: number;
  limit: number;
  /** índice do bloco na grade */
  block: number;
}

/**
 * Peso de um pedido no limitador do servidor: 2 por lote (documentação da Binance, GET /api/v3/klines: "IP Weight 2",
 * limit máx. 1.000); o servidor busca em lotes de até `porLote` velas, um esperando o outro.
 */
export function pesoDoPedido(limit: number, porLote = MAX_CANDLES_PER_REQUEST): number {
  return 2 * Math.max(1, Math.ceil(limit / porLote));
}

/** Aberturas necessárias para a vista com a vela de T no centro: [rs, re) (perto do presente o centro encosta no fim). */
export function necessarioGrade(T: number, iv: number, now: number): { rs: number; re: number } {
  const newest = Math.floor(now / iv) * iv;                        // abertura da vela de agora
  const c = Math.min(Math.floor(T / iv) * iv, newest - (NEED_NEWER - 1) * iv);
  return { rs: c - NEED_OLDER * iv, re: c + NEED_NEWER * iv };
}

/**
 * Janela final [ini, fim) de aberturas: W velas em volta da vela de T (ou terminando no presente, sem T). `dir` = a
 * direção da vista antes da troca (+1 rumo ao presente; -1 ou 0: o passado). Puro.
 */
export function janelaFinal(T: number | null, iv: number, now: number, W: number, dir: number): { ini: number; fim: number } {
  const newest = Math.floor(now / iv) * iv;
  if (T === null) return { ini: newest - (W - 1) * iv, fim: newest + iv };
  const c = Math.floor(T / iv) * iv;
  const half = NAV_VISIBLE_CANDLES / 2;                             // vista: [c − 499, c + 500]
  const older = dir === 1 ? W - NAV_VISIBLE_CANDLES - AHEAD_IDLE : AHEAD_IDLE + WARMUP;
  let ini = c - (half - 1 + older) * iv, fim = ini + W * iv;
  if (fim > newest + iv) { fim = newest + iv; ini = fim - W * iv; }  // encosta no presente
  return { ini, fim };
}

/** Os pedidos da troca (do mais antigo ao mais novo) e a janela final. `W` limitado a (MAX_BLOCKS − 1) blocos. Puro. */
export function planoJanela(T: number | null, iv: number, now: number, W: number, dir: number, G = GRID_CANDLES): { pedidos: PedidoGrade[]; ini: number; fim: number } {
  W = Math.min(W, (MAX_BLOCKS - 1) * G);
  const { ini, fim } = janelaFinal(T, iv, now, W, dir);
  const Gm = G * iv, newest = Math.floor(now / iv) * iv;
  const lo = Math.floor(ini / Gm), hi = Math.floor((fim - iv) / Gm), j = Math.floor(newest / Gm);
  const pedidos: PedidoGrade[] = [];
  // F3e C2: blocos antes de 1970 não são pedidos (endTime negativo = 400 na Binance; antes do F3e virava "bloqueio")
  for (let b = Math.max(lo, 0); b <= Math.min(hi, j - 1); b++) pedidos.push({ key: `b${b}`, kind: 'bloco', before: (b + 1) * Gm - 1, limit: G, block: b });
  if (hi >= j) pedidos.push({ key: 'p', kind: 'presente', limit: G, block: j });
  return { pedidos, ini, fim };
}

export interface CargaOpts {
  maxInFlight?: number; retryDelays?: number[]; maxFails?: number; giveUpMs?: number;
}

/**
 * Carga de uma troca: a janela final pelos blocos da grade, em paralelo (até MAX_IN_FLIGHT em voo). Guarda o que já
 * chegou: chamar `run()` de novo (o "tentar de novo") pede SÓ o que falta. `cancel()` (outra troca começou): nada mais
 * é pedido nem repetido e a carga não termina mais.
 */
export class CargaGrade {
  readonly plano: PedidoGrade[];
  readonly ini: number; readonly fim: number;
  /** pedidos feitos (com as repetições), peso somado no limitador do servidor e falhas */
  requests = 0; weight = 0; falhas = 0;
  /** ?dbg=1: cada pedido que volta (ok = não falhou; vazio conta como resposta) */
  onResposta: ((p: PedidoGrade, ok: boolean) => void) | null = null;
  /** progresso para o aviso: pedidos com resposta / total */
  onProgresso: ((feitos: number, total: number) => void) | null = null;
  private got = new Map<string, RawCandle[]>();
  private curto = false;
  private cancelado = false;
  private readonly o: Required<CargaOpts>;
  constructor(private readonly src: CandleSource, private readonly req: { symbol: string; interval: string; intervalMs: number; T: number | null; now: number; window: number; dir: number }, opts: CargaOpts = {}) {
    const pl = planoJanela(req.T, req.intervalMs, req.now, req.window, req.dir);
    this.plano = pl.pedidos; this.ini = pl.ini; this.fim = pl.fim;
    this.o = { maxInFlight: opts.maxInFlight ?? MAX_IN_FLIGHT, retryDelays: opts.retryDelays ?? RETRY_DELAYS, maxFails: opts.maxFails ?? MAX_FAILS, giveUpMs: opts.giveUpMs ?? GIVE_UP_MS };
  }
  cancel(): void { this.cancelado = true; }
  feitos(): number { let n = 0; for (const p of this.plano) if (this.got.has(p.key)) n++; return n; }
  /**
   * pedidos ainda sem resposta boa. O do PRESENTE nunca é guardado entre tentativas: numa nova tentativa ele vem de
   * novo (o ao vivo começa na vela de agora; um presente de segundos atrás deixaria um buraco antes dela)
   */
  pendentes(): PedidoGrade[] {
    const falta = this.plano.filter(p => !this.got.has(p.key));
    return falta.length && falta.length < this.plano.length ? this.plano.filter(p => p.kind === 'presente' || !this.got.has(p.key)) : falta;
  }
  private fetch(p: { before?: number; limit: number }) {
    this.requests++; this.weight += pesoDoPedido(p.limit);
    return this.src.fetchBlock({ symbol: this.req.symbol, interval: this.req.interval, before: p.before, limit: p.limit });
  }
  /** pede `pend` com até maxInFlight em voo; repete só o que falha; desiste com maxFails falhas ou giveUpMs */
  private lote(pend: PedidoGrade[]): Promise<Extract<SerieEmVolta, { kind: 'erro' }> | null> {
    const { maxInFlight, retryDelays, maxFails, giveUpMs } = this.o;
    for (const p of pend) this.got.delete(p.key);
    return new Promise(resolve => {
      const queue = [...pend], tries = new Map<string, number>();
      let inFlight = 0, waiting = 0, done = false;
      const finish = (r: Extract<SerieEmVolta, { kind: 'erro' }> | null) => { if (!done) { done = true; clearTimeout(timer); resolve(r); } };
      const timer = setTimeout(() => finish({ kind: 'erro', tipo: 'rede', status: null, message: `tempo esgotado (${giveUpMs / 1000} s)`, requests: this.requests }), giveUpMs);
      const next = () => {
        if (done || this.cancelado) return;
        while (inFlight < maxInFlight && queue.length) { const p = queue.shift()!; inFlight++; one(p); }
        if (!inFlight && !waiting && !queue.length) finish(null);
      };
      const one = (p: PedidoGrade) => this.fetch(p).then(r => {
        inFlight--;
        if (this.onResposta) this.onResposta(p, r.kind !== 'erro');
        if (this.cancelado) return;
        if (r.kind === 'erro') {
          this.falhas++;
          if (done) return;
          if (this.falhas >= maxFails) return finish({ kind: 'erro', tipo: r.tipo, status: r.status, message: r.message, requests: this.requests });
          const k = tries.get(p.key) ?? 0; tries.set(p.key, k + 1);
          waiting++;
          setTimeout(() => { waiting--; if (done || this.cancelado) return; queue.unshift(p); next(); }, retryDelays[Math.min(k, retryDelays.length - 1)]);
          return next();
        }
        const c = r.kind === 'ok' ? r.candles : [];
        if (c.length < p.limit) this.curto = true;
        this.got.set(p.key, c);                                   // guarda mesmo depois de desistir (o "tentar de novo" aproveita)
        if (this.onProgresso && !done) this.onProgresso(this.feitos(), this.plano.length);
        next();
      });
      next();
    });
  }
  async run(): Promise<SerieEmVolta | 'reserva'> {
    this.falhas = 0;
    const fail = await this.lote(this.pendentes());
    if (fail) return fail;
    const atPresent = this.plano.some(p => p.kind === 'presente');
    // houve repetição (2 s, 4 s): o bloco do presente que chegou antes ficou velho; o ao vivo começa na vela de agora,
    // então o presente vem de novo (senão sobra um buraco antes da 1ª vela ao vivo)
    if (atPresent && this.falhas > 0) {
      const again = await this.lote(this.plano.filter(p => p.kind === 'presente'));
      if (again) return again;
    }
    let all = juntarBlocos(this.plano.map(p => this.got.get(p.key)!));
    if (atPresent) {
      // o bloco do presente vem da fonte (a vela de agora DELA); os da grade, do relógio do aparelho. Relógio atrasado:
      // buraco entre o último bloco da grade e o presente -> pede as velas antes do presente até juntar (até 3 vezes)
      const pr = this.got.get('p') ?? [];
      for (let k = 0; k < 3 && pr.length && !this.curto; k++) {
        const first = pr[0][0], i = all.findIndex(c => c[0] === first);
        if (i <= 0 || all[i - 1][0] >= first - this.req.intervalMs) break;
        const r = await this.fetch({ before: first, limit: GRID_CANDLES });
        if (this.onResposta) this.onResposta({ key: `j${k}`, kind: 'complemento', before: first, limit: GRID_CANDLES, block: -1 }, r.kind !== 'erro');
        if (r.kind === 'erro') return { kind: 'erro', tipo: r.tipo, status: r.status, message: r.message, requests: this.requests };
        if (r.kind !== 'ok' || !r.candles.length) break;
        this.got.set(`j${k}`, r.candles); pr.unshift(...r.candles);
        all = juntarBlocos([all, r.candles]);
      }
    }
    // com o presente: as últimas W velas que a fonte entregou (sem depender do relógio do aparelho); sem: [ini, fim)
    const candles = atPresent ? all.slice(-Math.round((this.fim - this.ini) / this.req.intervalMs)) : all.filter(c => c[0] >= this.ini && c[0] < this.fim);
    const ok = (cs: RawCandle[], pres: boolean): SerieEmVolta => ({ kind: 'ok', candles: cs, atPresent: pres, startKnown: null, requests: this.requests });
    if (!this.curto && candles.length) return ok(candles, atPresent);   // nada pedido/chegado (antes de 1970): como tudo vazio
    const T = this.req.T, iv = this.req.intervalMs;
    const rs = T === null ? -Infinity : necessarioGrade(T, iv, this.req.now).rs;
    if (candles.length && (candles[0][0] <= rs || atPresent || candles.length >= NEED_OLDER + NEED_NEWER)) return ok(candles, atPresent);
    if (candles.length || atPresent) return 'reserva';
    // tudo vazio: T antes do começo do ativo. Série curta? (as mais novas vêm com menos que o pedido = a série inteira)
    const pr = await this.fetch({ limit: GRID_CANDLES });
    if (this.onResposta) this.onResposta({ key: 'p2', kind: 'presente', limit: GRID_CANDLES, block: -1 }, pr.kind !== 'erro');
    if (pr.kind === 'erro') return { kind: 'erro', tipo: pr.tipo, status: pr.status, message: pr.message, requests: this.requests };
    if (pr.kind === 'ok' && pr.candles.length > 0 && pr.candles.length < GRID_CANDLES) return ok(pr.candles, true);
    return 'reserva';
  }
}

type FetchBlockResultLike = Awaited<ReturnType<CandleSource['fetchBlock']>>;

/** Junta os blocos (podem se sobrepor quando a fonte atravessa uma lacuna): ordem crescente, sem vela repetida. */
export function juntarBlocos(parts: readonly (readonly RawCandle[])[]): RawCandle[] {
  const all: RawCandle[] = [];
  for (const p of parts) for (const c of p) all.push(c);
  all.sort((a, b) => a[0] - b[0]);
  const out: RawCandle[] = [];
  for (const c of all) { if (out.length && out[out.length - 1][0] === c[0]) out[out.length - 1] = c; else out.push(c); }
  return out;
}
